import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { authApi } from '../api/client.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Field } from '../components/Field.js';
import { FormMessage } from '../components/FormMessage.js';
import { SubmitButton } from '../components/SubmitButton.js';
import type { ForgotValues } from '../auth/schemas.js';
import { useAuthSchemas } from '../auth/useAuthSchemas.js';
import { useI18n } from '../i18n/context.js';

export function ForgotPasswordPage(): JSX.Element {
  const { forgot: forgotSchema } = useAuthSchemas();
  const { t } = useI18n();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotValues>({ resolver: zodResolver(forgotSchema) });

  const onSubmit = handleSubmit(async (values) => {
    // Исход намеренно один и тот же при любой ошибке: экран не должен
    // подсказывать, зарегистрирован адрес или нет (01-SPEC.md §5.2).
    try {
      await authApi.forgotPassword(values.email);
    } finally {
      setSent(true);
    }
  });

  return (
    <AuthLayout
      title={t.auth.forgot.title}
      subtitle={sent ? undefined : t.auth.forgot.subtitle}
      footer={<Link to="/login">← {t.auth.backToSignIn}</Link>}
    >
      {sent ? (
        <FormMessage kind="success">{t.auth.forgot.sent}</FormMessage>
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <Field
            label={t.common.email}
            type="email"
            autoComplete="email"
            placeholder={t.common.emailPlaceholder}
            error={errors.email?.message}
            {...register('email')}
          />
          <SubmitButton label={t.auth.forgot.submit} pending={isSubmitting} />
        </form>
      )}
    </AuthLayout>
  );
}
